import type { TOC } from '@ember/component/template-only';
import NavCard from 'tio-ui/components/nav-card';
import { t } from 'ember-intl';
import {
  PhBookOpen,
  PhCurrencyDollar,
  PhGraduationCap,
  PhHandshake,
  PhHeart,
  PhPiggyBank,
  PhShieldCheck,
} from 'ember-phosphor-icons';
import type { BenefitRows } from 'tio-employee/utils/beta/benefit-rows';

interface BenefitCardsSignature {
  Args: {
    /** The rows the side nav shows this user; each true row renders one card. */
    rows: BenefitRows;
  };
  Element: HTMLDivElement;
}

/**
 * The products a user's side nav lists, as navigating cards. Loan Summary is
 * omitted because this grid renders on that page.
 */
const BenefitCards: TOC<BenefitCardsSignature> = <template>
  <div class="grid grid-cols-2 gap-3 md:grid-cols-4" data-test-benefit-cards ...attributes>
    {{#if @rows.tuitionAssistance}}
      <NavCard
        @route="authenticated.tas.dashboard"
        @icon={{PhGraduationCap}}
        @title={{t "sidebar.tuition_assistance"}}
        @subtitle={{t "accounts.loan_summary.benefits.tuition_assistance"}}
        data-test-benefit-card-tuition-assistance
      />
    {{/if}}
    {{#if @rows.wellness}}
      <NavCard
        @route="authenticated.wellness-insights"
        @icon={{PhHeart}}
        @title={{t "sidebar.loan_wellness"}}
        @subtitle={{t "accounts.loan_summary.benefits.wellness"}}
        data-test-benefit-card-wellness
      />
    {{/if}}
    {{#if @rows.pslf}}
      <NavCard
        @route="authenticated.pslf.dashboard"
        @icon={{PhShieldCheck}}
        @title={{t "sidebar.loan_forgiveness"}}
        @subtitle={{t "accounts.loan_summary.benefits.pslf"}}
        data-test-benefit-card-pslf
      />
    {{/if}}
    {{#if @rows.slr}}
      <NavCard
        @route="authenticated.slr.dashboard"
        @icon={{PhHandshake}}
        @title={{t "sidebar.loan_repayment"}}
        @subtitle={{t "accounts.loan_summary.benefits.slr"}}
        data-test-benefit-card-slr
      />
    {{/if}}
    {{#if @rows.learningPartners}}
      <NavCard
        @route="authenticated.lpn.index"
        @icon={{PhBookOpen}}
        @title={{t "sidebar.learning_partners"}}
        @subtitle={{t "accounts.loan_summary.benefits.learning_partners"}}
        data-test-benefit-card-learning-partners
      />
    {{/if}}
    {{#if @rows.syf}}
      <NavCard
        @route="authenticated.syf.dashboard"
        @icon={{PhPiggyBank}}
        @title={{t "sidebar.secure_your_future"}}
        @subtitle={{t "accounts.loan_summary.benefits.syf"}}
        data-test-benefit-card-syf
      />
    {{/if}}
    {{#if @rows.idr}}
      <NavCard
        @route="authenticated.idr.dashboard"
        @icon={{PhCurrencyDollar}}
        @title={{t "sidebar.income_driven_repayment"}}
        @subtitle={{t "accounts.loan_summary.benefits.idr"}}
        data-test-benefit-card-idr
      />
    {{/if}}
  </div>
</template>;

export default BenefitCards;
